/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { HiOutlinePencil, HiOutlineTrash, HiOutlineUserGroup } from "react-icons/hi";

import ListPage from "../../layout/ListPage";
import { Avatar } from "../../ui";
import type { Action, Column } from "../../ui";
import { useAdminContext } from "../../context/admin/AdminContext";
import { useAdminsContext } from "../../context/admin/AdminManageContext";
import { pushOneAdmin } from "../../lib/sysNavigation/SysNavigation";
import { _PAGE_SIZE_ } from "../../lib/other/tools";
// @ts-ignore — untyped JS helper.
import { SearchMatchingAdmins } from "./SearchFunction";

type AdminRow = {
  admin_id: number | string;
  first_name?: string;
  last_name?: string;
  createdAt?: string;
  [key: string]: unknown;
};

const addRoute = "/admin/add";
const editRoute = "/admin/edit";

const Overview: React.FC = () => {
  const navigate = useNavigate();
  const { sysAdmin } = useAdminContext();
  const { admins, count, getAllByAdminCompanyId, setAdmins, deleteAdmin } =
    useAdminsContext() as any;

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<number>(_PAGE_SIZE_);
  const [query, setQuery] = useState("");

  useEffect(() => {
    if (!admins) getAllByAdminCompanyId(page, pageSize);
  }, [admins]);

  const rows = useMemo<AdminRow[] | null>(() => {
    if (!Array.isArray(admins)) return null;
    if (!query.trim()) return admins as AdminRow[];
    return SearchMatchingAdmins(query, admins) as AdminRow[];
  }, [admins, query]);

  const handleDelete = async (row: AdminRow) => {
    const response: any = await deleteAdmin(row.admin_id);
    if ((response?.status === 200 || response?.status === 201) && Array.isArray(admins)) {
      setAdmins((admins as AdminRow[]).filter((a) => a.admin_id !== row.admin_id));
    }
  };

  const columns: Column<AdminRow>[] = [
    {
      header: "Name",
      cell: (row) => (
        <div className="flex items-center gap-3">
          <Avatar firstName={row.first_name} lastName={row.last_name} size="sm" />
          <div className="min-w-0">
            <p className="truncate font-medium text-gray-900">
              {`${row.first_name ?? ""} ${row.last_name ?? ""}`.trim() || "—"}
            </p>
            <p className="text-[11px] text-gray-500">Administrator</p>
          </div>
        </div>
      ),
    },
    {
      header: "Joined",
      cell: (row) =>
        row.createdAt ? (
          <span className="text-gray-600">
            {new Date(row.createdAt).toDateString()}
          </span>
        ) : (
          <span className="text-gray-300">—</span>
        ),
    },
  ];

  const rowActions = (row: AdminRow): Action[] => [
    {
      label: "Edit",
      icon: <HiOutlinePencil className="h-3.5 w-3.5" />,
      onClick: () => {
        pushOneAdmin(row);
        navigate(editRoute);
      },
    },
    {
      label: "Delete",
      variant: "destructive",
      icon: <HiOutlineTrash className="h-3.5 w-3.5" />,
      disabled: row.admin_id === sysAdmin?.admin_id,
      onClick: () => handleDelete(row),
      confirm: {
        title: `Remove ${row.first_name ?? ""} ${row.last_name ?? ""}?`,
        description: "Their access to the dashboard will be revoked immediately.",
        confirmLabel: "Remove",
      },
    },
  ];

  return (
    <ListPage<AdminRow>
      title="Admins"
      subtitle="Colleagues with access to this dashboard."
      icon={<HiOutlineUserGroup className="h-5 w-5" />}
      actions={[{ label: "Add admin", variant: "primary", href: addRoute }]}
      rows={rows}
      columns={columns}
      rowKey={(r) => r.admin_id}
      rowActions={rowActions}
      search={{
        value: query,
        onChange: setQuery,
        placeholder: "Search admins by name or email…",
      }}
      pagination={{
        page,
        pageSize,
        total: count,
        onChange: ({ page: p, pageSize: ps }) => {
          setPage(p);
          setPageSize(ps);
          getAllByAdminCompanyId(p, ps);
        },
      }}
      empty="No admins yet."
    />
  );
};

export default Overview;
